/**
 * DataManager — Real-data upload control center.
 *
 * Counter staff / data-entry operators come here to:
 *   1. Wipe all demo/dummy data (one-time per dataset migration).
 *   2. Download the .xlsx or .csv sample template for each module.
 *   3. Bulk-upload a filled-in workbook.
 *   4. Manually add a single row when only one record needs entering.
 *
 * One page, nine modules, three actions each — kept intentionally simple.
 */
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { getUser } from "@/lib/auth";
import {
  DownloadSimple, UploadSimple, Plus, Trash, FileXls, FileCsv,
  Warning, CheckCircle, Bus, MapPinLine, Path, Clock,
  UserCircle, Buildings, BuildingApartment, Briefcase, CurrencyInr,
  X, ArrowsClockwise, Files, FilePdf, FileText, Funnel,
  PencilSimple, MagnifyingGlass,
} from "@phosphor-icons/react";

// Module catalogue mirrors backend/data_loader.py:MODULES. Adding a new
// module here is a 1-line change (icon + description) once the backend
// side has been registered.
const MODULES = [
  { key: "buses",       label: "Fleet — Buses",                         icon: Bus,              about: "Vehicle registrations, capacity, depot, GPS device ID. Natural key = registration_number." },
  { key: "bus-stops",   label: "Bus Stops",                             icon: MapPinLine,       about: "Stop code, name, coordinates, locality. Supports KTCL's bidirectional format — fill onward_stop_lat/lon + return_stop_lat/lon and each row becomes 2 GTFS-compliant stops (\u00abCODE\u00bb_O for onward, \u00abCODE\u00bb_R for return). Or use plain lat/lng for single-direction stops." },
  { key: "routes",      label: "Routes",                                icon: Path,             about: "Route number, name, depot & bus type — 4 columns only. route_id is auto-generated from route_no (e.g. \"21A\" → R-21A). Natural key = route_no." },
  { key: "trips",       label: "Trips & Stop-Times",                    icon: Clock,            about: "One row per stop-time. Rows sharing the same trip_id are grouped into a trip with stop_times." },
  { key: "staff-field", label: "Field Staff (Drivers + Conductors)",    icon: UserCircle,       about: "Driver/conductor/inspector roster. The 'role' column routes the record to the right Mongo collection." },
  { key: "staff-hq",    label: "HQ / Backend Staff",                    icon: BuildingApartment, about: "Office, depot manager, IT, finance roster. Stored in hq_staff." },
  { key: "depots",      label: "Depots",                                icon: Buildings,        about: "Depot code, address, manager, lat/lng. Natural key = code." },
  { key: "operators",   label: "Operators / Partners",                  icon: Briefcase,        about: "KTCL primary + any private operators. Natural key = code." },
  { key: "fares",       label: "Fares (route stop-pair)",               icon: CurrencyInr,      about: "Optional. Used as the override for the official-fare lookup. Natural key = route_id|from_stop|to_stop." },
];

export default function DataManager() {
  const [tab, setTab] = useState("buses");
  const [showWipe, setShowWipe] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [wiping, setWiping] = useState(false);
  // ---- Safety guards on the destructive Wipe ----
  const adminEmail = (getUser()?.email || "").toLowerCase();
  const [wipeCounts, setWipeCounts] = useState(null);     // live counts shown in the dialog
  const [wipeCountdown, setWipeCountdown] = useState(0);  // 5-second hold before the red button enables
  const [lastResult, setLastResult] = useState(null);
  const [counts, setCounts] = useState({});

  // Manual-add modal state
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({});

  // Preview / confirm-before-upload state
  const [preview, setPreview] = useState(null);   // { module, total_rows, will_insert, will_update, ... }
  const [pendingFile, setPendingFile] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);

  const fileRef = useRef(null);

  // Module schemas — we hit /admin/data/<key>/template.csv once to learn the
  // columns so we can drive the Manual Add modal without duplicating schema.
  const [schema, setSchema] = useState({});
  useEffect(() => {
    if (schema[tab]) return;
    api.get(`/admin/data/${tab}/template.csv`, { responseType: "text" })
      .then(({ data }) => {
        const headers = (data || "").split("\n")[0].split(",").map((h) => h.trim());
        const example = ((data || "").split("\n")[1] || "").split(",").map((v) => v.trim());
        setSchema((s) => ({ ...s, [tab]: headers.map((h, i) => ({ header: h, example: example[i] })) }));
      })
      .catch(() => setSchema((s) => ({ ...s, [tab]: [] })));
  }, [tab, schema]);

  const download = async (fmt) => {
    try {
      const { data } = await api.get(`/admin/data/${tab}/template.${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ktcl_${tab}_template.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${tab} ${fmt.toUpperCase()} template`);
    } catch (e) {
      toast.error(`Failed to download template: ${e?.message}`);
    }
  };

  // STEP 1 — preview the file (dry-run). Always called first, before any
  // writes happen. The result drives the confirmation modal.
  const handleFileSelected = async (file) => {
    if (!file) return;
    setPendingFile(file);
    setPreviewing(true);
    setPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/data/${tab}/preview`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
      setPendingFile(null);
    } finally {
      setPreviewing(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  // STEP 2 — commit. Only fires when the operator clicks "Proceed" in the
  // confirmation modal AFTER seeing the insert/update/duplicate breakdown.
  const commitUpload = async () => {
    if (!pendingFile) return;
    setCommitting(true);
    try {
      const fd = new FormData();
      fd.append("file", pendingFile);
      const { data } = await api.post(`/admin/data/${tab}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLastResult(data);
      const errs = (data.errors || []).length;
      const dupes = (data.within_file_duplicates || []).length;
      if (errs === 0 && dupes === 0) {
        toast.success(`Imported ${data.inserted_or_updated} ${tab} rows`);
      } else if (errs === 0) {
        toast.warning(`Imported ${data.inserted_or_updated} rows · ${dupes} within-file duplicates auto-resolved`);
      } else {
        toast.warning(`Imported ${data.inserted_or_updated} rows · ${errs} errors — see report below`);
      }
      setPreview(null);
      setPendingFile(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setCommitting(false);
    }
  };

  const cancelPreview = () => {
    setPreview(null);
    setPendingFile(null);
  };

  const wipe = async () => {
    // Confirmation: must type the admin's OWN email (case-insensitive)
    if (confirm.trim().toLowerCase() !== adminEmail) return;
    // 5-second hold must have elapsed (handled by button-disabled state too)
    if (wipeCountdown > 0) return;
    setWiping(true);
    try {
      // Backend still expects the legacy phrase — send it on behalf of the
      // operator who already passed the harder email check on the client.
      const { data } = await api.post("/admin/data/wipe", { confirm: "WIPE-KTCL-DATA" });
      const total = Object.values(data.deleted || {}).reduce((s, v) => s + (typeof v === "number" ? v : 0), 0);
      toast.success(`Wipe complete — ${total} rows removed across ${Object.keys(data.deleted || {}).length} collections`);
      setLastResult({ ok: true, wipe: data });
      setShowWipe(false);
      setConfirm("");
      setWipeCounts(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wipe failed");
    } finally {
      setWiping(false);
    }
  };

  // When the wipe dialog opens, fetch live counts so the operator sees EXACTLY
  // what will be deleted, and start the 5-second confirmation hold.
  const openWipeDialog = async () => {
    setShowWipe(true);
    setConfirm("");
    setWipeCounts(null);
    setWipeCountdown(5);
    try {
      const { data } = await api.get("/dashboard/data-health");
      // Flatten {collection, count} → {collection: count} for quick lookup
      const flat = {};
      (data?.modules || []).forEach((m) => { flat[m.collection] = m.count; });
      setWipeCounts(flat);
    } catch {
      setWipeCounts({});
    }
  };

  // 5-second countdown tick — the destructive button stays disabled until 0.
  useEffect(() => {
    if (!showWipe || wipeCountdown <= 0) return;
    const t = setTimeout(() => setWipeCountdown((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [showWipe, wipeCountdown]);

  const manualAdd = async () => {
    try {
      const { data } = await api.post(`/admin/data/${tab}/add`, form);
      toast.success(`Added ${tab} record`);
      setLastResult({ ok: true, manual: data });
      setShowAdd(false);
      setForm({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Manual add failed");
    }
  };

  // Esc key closes any open modal — small UX nicety matching the rest of admin.
  useEffect(() => {
    if (!showWipe && !showAdd && !preview) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (showAdd) { setShowAdd(false); setForm({}); }
        if (showWipe) { setShowWipe(false); setConfirm(""); }
        if (preview && !committing) { cancelPreview(); }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showWipe, showAdd, preview, committing]);

  const current = MODULES.find((m) => m.key === tab);
  const Icon = current.icon;
  const cols = schema[tab] || [];

  return (
    <div className="p-4 lg:p-6 space-y-4 lg:space-y-5" data-testid="data-manager">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="label-cap text-slate-500">Real-Data Loader</div>
          <h1 className="font-display text-xl lg:text-2xl font-bold">Data Manager</h1>
          <p className="text-[12px] lg:text-sm text-muted-foreground mt-1 max-w-2xl">
            Upload your own KTCL master-data via Excel or CSV — one tab per module.
            Demo data has been disabled; this page is now the canonical entry point.
          </p>
        </div>
        <button onClick={openWipeDialog}
          data-testid="data-wipe-open"
          className="px-3 py-2 rounded-md text-[12px] font-bold bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 inline-flex items-center gap-1.5">
          <Trash size={13} weight="bold"/> Reset to Blank
        </button>
      </div>

      {/* Fleet Report Export — CSV / XLSX / PDF / JSON download */}
      <FleetReportExport />

      {/* Module tabs */}
      <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-9 gap-2" data-testid="data-module-tabs">
        {MODULES.map((m) => {
          const I = m.icon;
          const active = tab === m.key;
          return (
            <button key={m.key}
              data-testid={`data-tab-${m.key}`}
              onClick={() => { setTab(m.key); setLastResult(null); }}
              className={`text-left rounded-md p-2 md:p-3 transition-all border ${
                active
                  ? "ktcl-btn-primary border-[#1E3A8A] shadow-sm"
                  : "bg-white border-slate-200 hover:border-[#1E3A8A] text-slate-700"
              }`}>
              <I size={20} weight={active ? "fill" : "duotone"}
                 className={active ? "text-white" : "text-[#1E3A8A]"}/>
              <div className="text-[10.5px] md:text-[12px] font-bold leading-tight mt-1.5">{m.label}</div>
            </button>
          );
        })}
      </div>

      {/* Current module — actions */}
      <div className="ktcl-card p-4 md:p-5 space-y-4">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-md bg-blue-50 border border-[#1E3A8A]/20 flex items-center justify-center shrink-0">
            <Icon size={26} weight="duotone" className="text-[#1E3A8A]"/>
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-display font-bold text-base md:text-lg">{current.label}</h2>
            <p className="text-[12px] text-slate-500 mt-0.5">{current.about}</p>
          </div>
        </div>

        {/* Action row — download + upload + manual add */}
        <div className={`grid grid-cols-2 ${tab === "routes" ? "md:grid-cols-3" : "md:grid-cols-4"} gap-2`}>
          <button onClick={() => download("xlsx")}
            data-testid={`data-download-xlsx-${tab}`}
            className="px-3 py-2.5 rounded-md text-[12px] font-bold bg-blue-50 text-[#1E3A8A] border border-[#1E3A8A]/20 hover:bg-blue-100 inline-flex items-center justify-center gap-1.5">
            <FileXls size={14} weight="bold"/> Sample .xlsx
          </button>
          <button onClick={() => download("csv")}
            data-testid={`data-download-csv-${tab}`}
            className="px-3 py-2.5 rounded-md text-[12px] font-bold bg-blue-50 text-[#1E3A8A] border border-[#1E3A8A]/20 hover:bg-blue-100 inline-flex items-center justify-center gap-1.5">
            <FileCsv size={14} weight="bold"/> Sample .csv
          </button>
          <label data-testid={`data-upload-${tab}`}
            className="ktcl-btn-primary px-3 py-2.5 rounded-md text-[12px] font-bold inline-flex items-center justify-center gap-1.5 cursor-pointer">
            <UploadSimple size={14} weight="bold"/>{previewing ? "Checking…" : "Bulk Upload"}
            <input ref={fileRef} type="file" accept=".xlsx,.csv" className="hidden"
              onChange={(e) => handleFileSelected(e.target.files?.[0])}/>
          </label>
          {tab !== "routes" && (
            <button onClick={() => { setForm({}); setShowAdd(true); }}
              data-testid={`data-add-${tab}`}
              className="px-3 py-2.5 rounded-md text-[12px] font-bold bg-white text-[#1E3A8A] border border-[#1E3A8A] hover:bg-blue-50 inline-flex items-center justify-center gap-1.5">
              <Plus size={14} weight="bold"/> Add Manually
            </button>
          )}
        </div>

        {/* Schema preview — hidden for Routes (replaced by the dedicated panel below) */}
        {tab !== "routes" && cols.length > 0 && (
          <div className="border border-slate-200 rounded-md overflow-hidden">
            <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-[11px] font-bold uppercase tracking-wider text-slate-600">
              Expected columns ({cols.length}) — your file&apos;s header row must match these exactly
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 p-3 text-[11.5px]">
              {cols.map((c, i) => (
                <div key={i} className="bg-slate-50 rounded px-2 py-1 truncate" title={c.header} data-testid={`data-col-${c.header}`}>
                  <span className="font-mono font-bold text-slate-700">{c.header}</span>
                  {c.example && <span className="text-slate-400 ml-1.5">e.g. {c.example}</span>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Dedicated Routes admin panel — Quick add + edit/delete with only 3 fields */}
      {tab === "routes" && <RoutesAdmin />}

      {/* Last result panel */}
      {lastResult && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-2" data-testid="data-result-panel">
          <div className="flex items-center gap-2 text-sm font-bold">
            <CheckCircle size={16} weight="fill" className="text-emerald-600"/> Last action result
          </div>
          <pre className="text-[11px] font-mono bg-slate-900 text-emerald-300 p-3 rounded-md overflow-x-auto max-h-80">
{JSON.stringify(lastResult, null, 2)}
          </pre>
        </div>
      )}

      {/* Preview / confirmation modal — fires BEFORE upload commits */}
      {preview && (
        <div className="fixed inset-0 bg-black/50 z-[1400] flex items-center justify-center p-4" data-testid="data-preview-dialog"
          onClick={() => { if (!committing) cancelPreview(); }}>
          <div className="bg-white rounded-md max-w-lg w-full p-5 space-y-3 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div>
                <div className="label-cap text-slate-500">Dry-run · No data written yet</div>
                <h2 className="font-display font-bold text-lg flex items-center gap-2">
                  <Files size={18} weight="duotone" className="text-[#1E3A8A]"/>
                  Review {pendingFile?.name}
                </h2>
              </div>
              <button onClick={cancelPreview} disabled={committing}
                className="w-8 h-8 rounded-md bg-slate-100 text-slate-500 inline-flex items-center justify-center disabled:opacity-40"
                aria-label="Close"><X size={16}/></button>
            </div>

            {/* Counts */}
            <div className="grid grid-cols-2 gap-2">
              <PreviewStat label="Total rows in file"     value={preview.total_rows} testid="prev-total" />
              <PreviewStat label="Valid rows"             value={preview.valid_rows} testid="prev-valid" tone="info" />
              <PreviewStat label="New (will INSERT)"      value={preview.will_insert} testid="prev-insert" tone="ok" />
              <PreviewStat label="Existing (will UPDATE)" value={preview.will_update} testid="prev-update" tone="warn" />
            </div>

            {/* Within-file duplicates */}
            {(preview.within_file_duplicates || []).length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3" data-testid="prev-within-dupes">
                <div className="flex items-center gap-1.5 text-[12px] font-bold text-amber-800 mb-1">
                  <Warning size={13} weight="fill"/> {preview.within_file_duplicates.length} within-file duplicate{preview.within_file_duplicates.length>1?"s":""}
                </div>
                <div className="text-[11px] text-amber-700 leading-relaxed">
                  Multiple rows share the same <code className="font-mono">{preview.natural_key}</code> — only the LAST occurrence will be kept. Edit your file to dedupe.
                </div>
                <ul className="mt-2 text-[11px] font-mono text-amber-900 max-h-32 overflow-y-auto space-y-0.5">
                  {(preview.within_file_duplicates || []).slice(0, 8).map((d, i) => (
                    <li key={i} data-testid={`prev-dupe-row-${i}`}>
                      <b>{d.natural_key}</b> — rows {d.rows.join(", ")}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Validation errors */}
            {(preview.validation_errors || []).length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3" data-testid="prev-errors">
                <div className="flex items-center gap-1.5 text-[12px] font-bold text-red-700 mb-1">
                  <Warning size={13} weight="fill"/> {preview.validation_errors.length} row{preview.validation_errors.length>1?"s":""} will be skipped
                </div>
                <ul className="text-[11px] font-mono text-red-900 max-h-32 overflow-y-auto space-y-0.5">
                  {(preview.validation_errors || []).slice(0, 8).map((e, i) => (
                    <li key={i}>Row {e.row}: {e.error}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Existing keys preview */}
            {preview.will_update > 0 && (preview.existing_sample || []).length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3" data-testid="prev-existing">
                <div className="text-[11px] font-bold text-[#1E3A8A] mb-1">
                  Will overwrite these existing {preview.natural_key}s:
                </div>
                <div className="flex flex-wrap gap-1">
                  {(preview.existing_sample || []).slice(0, 10).map((k) => (
                    <span key={k} className="text-[10.5px] font-mono bg-white border border-blue-200 rounded px-1.5 py-0.5">{k}</span>
                  ))}
                  {preview.will_update > 10 && (
                    <span className="text-[10.5px] text-slate-500">…and {preview.will_update - 10} more</span>
                  )}
                </div>
              </div>
            )}

            {/* CTA row */}
            <div className="flex gap-2 pt-2 sticky bottom-0 bg-white">
              <button onClick={cancelPreview} disabled={committing}
                data-testid="prev-cancel"
                className="flex-1 px-3 py-2 rounded-md text-sm border border-slate-200 hover:bg-slate-50 disabled:opacity-50">
                Cancel
              </button>
              <button onClick={commitUpload}
                disabled={committing || preview.valid_rows === 0}
                data-testid="prev-confirm"
                className="ktcl-btn-primary flex-1 px-3 py-2 rounded-md text-sm font-bold disabled:opacity-50">
                {committing
                  ? "Uploading…"
                  : `Proceed · ${preview.valid_rows} row${preview.valid_rows===1?"":"s"}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Wipe confirmation modal */}
      {showWipe && (
        <div className="fixed inset-0 bg-black/60 z-[1400] flex items-center justify-center p-4" data-testid="data-wipe-dialog"
          onClick={() => { setShowWipe(false); setConfirm(""); setWipeCounts(null); }}>
          <div className="bg-white rounded-md max-w-lg w-full p-5 space-y-3 max-h-[92vh] overflow-y-auto"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <Warning size={20} weight="fill" className="text-red-600"/>
              </div>
              <div>
                <h3 className="font-display font-bold text-lg text-red-700">DANGER · Permanent Data Wipe</h3>
                <p className="text-[11.5px] text-slate-500">This is irreversible. There is no automatic undo.</p>
              </div>
            </div>

            {/* Live counts of what will be lost — fetched the moment the dialog opens */}
            <div className="text-[12.5px] bg-red-50 border-2 border-red-300 rounded-md p-3 space-y-2"
                 data-testid="data-wipe-impact">
              <div className="font-bold text-red-800 uppercase text-[11px] tracking-wider">
                You are about to delete:
              </div>
              {wipeCounts === null ? (
                <div className="text-slate-500 text-[12px]">Loading current counts…</div>
              ) : (
                <div className="grid grid-cols-3 gap-1.5 font-mono text-[11px]">
                  {[
                    ["buses",       wipeCounts.buses],
                    ["bus_stops",   wipeCounts.bus_stops],
                    ["routes",      wipeCounts.routes],
                    ["trips",       wipeCounts.trips],
                    ["drivers",     wipeCounts.drivers],
                    ["conductors",  wipeCounts.conductors],
                    ["hq_staff",    wipeCounts.hq_staff],
                    ["operators",   wipeCounts.operators],
                    ["fares",       wipeCounts.fares],
                    ["tickets",     wipeCounts.tickets],
                    ["passes",      wipeCounts.passes],
                    ["cards",       wipeCounts.transit_cards],
                  ].map(([k, v]) => (
                    <div key={k} className="bg-white border border-red-200 rounded px-2 py-1">
                      <div className="text-[9.5px] uppercase text-red-700 font-bold">{k}</div>
                      <div className="text-[14px] font-bold text-red-900 tabular-nums">
                        {Number(v || 0).toLocaleString("en-IN")}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="pt-1.5 border-t border-red-200 text-[11px] text-red-900">
                <b>Kept:</b> admin login, depot config, transit-card fee &amp; deposit, pass categories.
              </div>
            </div>

            {/* Type-your-own-email confirmation — much harder to muscle-memory */}
            <label className="block">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-700">
                Type your admin email exactly to confirm
              </span>
              <div className="mt-1.5 text-[10.5px] text-slate-500 font-mono">
                Expected: <b>{adminEmail || "(no email on session — please log in again)"}</b>
              </div>
              <input value={confirm} onChange={(e) => setConfirm(e.target.value)}
                data-testid="data-wipe-input"
                placeholder={adminEmail || "admin email"}
                autoComplete="off" spellCheck={false}
                className="w-full mt-1.5 px-3 py-2 border-2 border-red-200 rounded-md font-mono text-sm outline-none focus:border-red-600"/>
            </label>

            {/* 5-second hold indicator */}
            {wipeCountdown > 0 && (
              <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2"
                   data-testid="data-wipe-countdown">
                Safety hold · button enables in <b className="tabular-nums">{wipeCountdown}s</b>…
              </div>
            )}

            <div className="flex gap-2 pt-1">
              <button onClick={() => { setShowWipe(false); setConfirm(""); setWipeCounts(null); }}
                autoFocus
                data-testid="data-wipe-cancel"
                className="flex-1 px-3 py-2 rounded-md text-sm font-bold border border-slate-200 hover:bg-slate-50">
                Cancel (recommended)
              </button>
              <button onClick={wipe}
                disabled={wiping || wipeCountdown > 0 || confirm.trim().toLowerCase() !== adminEmail || !adminEmail}
                data-testid="data-wipe-confirm"
                className="flex-1 px-3 py-2 rounded-md text-sm font-bold bg-red-600 text-white hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed">
                {wiping ? "Wiping…" : wipeCountdown > 0 ? `Wait ${wipeCountdown}s` : "Yes, permanently delete"}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Manual Add modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/50 z-[1400] flex items-center justify-center p-4" data-testid="data-add-dialog"
          onClick={() => { setShowAdd(false); setForm({}); }}>
          <div className="bg-white rounded-md max-w-md w-full p-5 space-y-3 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-bold text-lg flex items-center gap-2">
              <Plus size={18} weight="bold" className="text-[#1E3A8A]"/>
              Add {current.label}
            </h3>
            <div className="space-y-2">
              {cols.map((c) => (
                <label key={c.header} className="block">
                  <span className="text-[10.5px] uppercase tracking-wider text-slate-500 font-bold">{c.header}</span>
                  <input value={form[c.header] ?? ""}
                    onChange={(e) => setForm({ ...form, [c.header]: e.target.value })}
                    placeholder={c.example || ""}
                    data-testid={`data-add-field-${c.header}`}
                    className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-md text-sm outline-none focus:border-[#1E3A8A]"/>
                </label>
              ))}
            </div>
            <div className="flex gap-2 pt-2 sticky bottom-0 bg-white">
              <button onClick={() => { setShowAdd(false); setForm({}); }}
                className="flex-1 px-3 py-2 rounded-md text-sm border border-slate-200 hover:bg-slate-50">
                Cancel
              </button>
              <button onClick={manualAdd}
                data-testid="data-add-confirm"
                className="ktcl-btn-primary flex-1 px-3 py-2 rounded-md text-sm font-bold">
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// RoutesAdmin — single-screen routes manager that lives inside Data Manager
// when the Routes tab is selected. Intentionally tiny:
//   • Quick-add: Route Number + Route Name + Depot (3 fields, that's it)
//   • Inline list with search + Edit + Delete on every row
// Uses the dedicated REST endpoints on /api/routes (POST · PATCH · DELETE).
// ---------------------------------------------------------------------------
function RoutesAdmin() {
  const [routes, setRoutes] = useState([]);
  const [depots, setDepots] = useState([]);
  const [busTypes, setBusTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [depotFilter, setDepotFilter] = useState("All");
  const [busTypeFilter, setBusTypeFilter] = useState("All");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);     // null = create mode; route_id string = edit mode
  const [form, setForm] = useState({ route_no: "", name: "", depot: "", bus_type: "" });
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  // Bus-types manager state
  const [showBT, setShowBT] = useState(false);
  const [newBT, setNewBT] = useState("");
  const [savingBT, setSavingBT] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: rs }, { data: ds }, { data: bts }] = await Promise.all([
        api.get("/routes"),
        api.get("/depots"),
        api.get("/bus-types"),
      ]);
      setRoutes(rs || []);
      setDepots((ds || []).map((d) => d.name || d.code).filter(Boolean));
      setBusTypes(bts || []);
    } catch {
      toast.error("Failed to load routes");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = routes.filter((r) => {
    if (depotFilter !== "All" && (r.depot || "") !== depotFilter) return false;
    if (busTypeFilter !== "All" && (r.bus_type || "") !== busTypeFilter) return false;
    if (!q.trim()) return true;
    const s = q.trim().toLowerCase();
    return (r.route_no || "").toLowerCase().includes(s)
      || (r.name || "").toLowerCase().includes(s)
      || (r.depot || "").toLowerCase().includes(s)
      || (r.bus_type || "").toLowerCase().includes(s)
      || (r.route_id || "").toLowerCase().includes(s);
  });

  // Counts per depot to drive the dropdown labels (so user sees row counts)
  const depotsInUse = Array.from(new Set(routes.map((r) => r.depot).filter(Boolean))).sort();
  const depotCount = (d) => routes.filter((r) => r.depot === d).length;
  // Bus types dropdown uses the OFFICIAL list from the bus_types collection,
  // not whatever happens to be sitting in route docs. This keeps the filter
  // tight (5 types only) and prevents legacy/typo'd values from polluting the
  // UI ("LOCAL INTERSTATE", "EV SHUTTLE", "AC LUXURY", etc.).
  const officialTypes = busTypes.map((bt) => bt.name);
  const busTypeCount = (name) => routes.filter((r) => (r.bus_type || "").toUpperCase() === name).length;
  // Routes that have bus_type values NOT in the official list — these were
  // imported with legacy/typo values and need cleanup so the filter stays tight.
  const officialSet = new Set(officialTypes.map((n) => n.toUpperCase()));
  const nonOfficialBusTypes = Array.from(new Set(
    routes
      .map((r) => (r.bus_type || "").trim())
      .filter((v) => v && !officialSet.has(v.toUpperCase()))
  )).sort();

  const clearNonOfficial = async () => {
    if (!window.confirm(`Clear bus_type on routes that use the ${nonOfficialBusTypes.length} non-official value(s)?\n\n${nonOfficialBusTypes.join(", ")}\n\nThese routes will keep all other data — only the bus_type field will be reset to blank so you can pick from the official 5.`)) return;
    try {
      const affected = routes.filter((r) => r.bus_type && !officialSet.has((r.bus_type || "").toUpperCase()));
      await Promise.all(affected.map((r) => api.patch(`/routes/${r.route_id}`, { bus_type: "" })));
      toast.success(`Cleared bus_type on ${affected.length} route(s)`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cleanup failed");
    }
  };

  const openCreate = () => { setEditing(null); setForm({ route_no: "", name: "", depot: depots[0] || "", bus_type: "" }); setShowForm(true); };
  const openEdit = (r) => { setEditing(r.route_id); setForm({ route_no: r.route_no || "", name: r.name || "", depot: r.depot || "", bus_type: r.bus_type || "" }); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setEditing(null); setForm({ route_no: "", name: "", depot: "", bus_type: "" }); };

  const save = async () => {
    const route_no = (form.route_no || "").trim();
    const name = (form.name || "").trim();
    const depot = (form.depot || "").trim();
    const bus_type = (form.bus_type || "").trim();
    if (!route_no) { toast.error("Route number is required"); return; }
    if (!name) { toast.error("Route name is required"); return; }
    if (!depot) { toast.error("Depot is required — pick the depot this route belongs to"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/routes/${editing}`, { route_no, name, depot, bus_type });
        toast.success(`Updated route ${route_no}`);
      } else {
        await api.post("/routes", { route_no, name, depot, bus_type });
        toast.success(`Added route ${route_no}`);
      }
      closeForm();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const del = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/routes/${confirmDel.route_id}`);
      toast.success(`Deleted ${confirmDel.route_no}`);
      setConfirmDel(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const addBusType = async () => {
    const name = (newBT || "").trim();
    if (!name) { toast.error("Type a bus type name"); return; }
    setSavingBT(true);
    try {
      await api.post("/bus-types", { name });
      toast.success(`Added bus type ${name.toUpperCase()}`);
      setNewBT("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add bus type");
    } finally { setSavingBT(false); }
  };

  const removeBusType = async (bt) => {
    try {
      await api.delete(`/bus-types/${bt.id}`);
      toast.success(`Removed ${bt.name}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cannot remove");
    }
  };

  return (
    <div className="ktcl-card p-4 md:p-5 space-y-3" data-testid="routes-admin">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="label-cap text-slate-500">Manage Routes</div>
          <h3 className="font-display font-bold text-base">Routes ({routes.length})</h3>
          <p className="text-[12px] text-slate-500 mt-0.5">Quick add — route number, name, depot &amp; bus type.</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
          <button onClick={() => setShowBT(true)}
            data-testid="bus-types-manage-btn"
            className="px-3 py-2 rounded-md text-[11.5px] font-bold bg-white text-[#1E3A8A] border border-[#1E3A8A] hover:bg-blue-50 inline-flex items-center gap-1.5">
            <Bus size={13} weight="bold"/>
            <span className="hidden sm:inline">Manage Bus Types</span>
            <span className="sm:hidden">Types</span>
          </button>
          <button onClick={openCreate}
            data-testid="route-add-btn"
            className="ktcl-btn-primary px-3 py-2 rounded-md text-[11.5px] font-bold inline-flex items-center gap-1.5">
            <Plus size={13} weight="bold"/> Add Route
          </button>
        </div>
      </div>

      {/* Filters — search + depot + bus type */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
        <div className="md:col-span-6 flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-md border border-slate-200">
          <MagnifyingGlass size={14} className="text-slate-400"/>
          <input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by number, name or depot…"
            data-testid="route-search"
            className="bg-transparent text-sm outline-none flex-1"/>
          {q && (
            <button onClick={() => setQ("")} className="text-slate-400 hover:text-slate-700">
              <X size={13}/>
            </button>
          )}
        </div>
        <select value={depotFilter}
          onChange={(e) => setDepotFilter(e.target.value)}
          data-testid="route-depot-filter"
          className="md:col-span-3 px-3 py-2 bg-white border border-slate-200 rounded-md text-sm outline-none focus:border-[#1E3A8A]">
          <option value="All">All depots ({routes.length})</option>
          {depotsInUse.map((d) => (
            <option key={d} value={d}>{d} ({depotCount(d)})</option>
          ))}
        </select>
        <select value={busTypeFilter}
          onChange={(e) => setBusTypeFilter(e.target.value)}
          data-testid="route-bus-type-filter"
          className="md:col-span-3 px-3 py-2 bg-white border border-slate-200 rounded-md text-sm outline-none focus:border-[#1E3A8A]">
          <option value="All">All bus types</option>
          {officialTypes.map((bt) => (
            <option key={bt} value={bt}>{bt} ({busTypeCount(bt)})</option>
          ))}
        </select>
      </div>

      {/* Active filter chips */}
      {(depotFilter !== "All" || busTypeFilter !== "All" || q.trim()) && (
        <div className="flex items-center gap-2 flex-wrap text-[11px]">
          <span className="text-slate-500">Showing {filtered.length} of {routes.length} routes</span>
          {depotFilter !== "All" && (
            <button onClick={() => setDepotFilter("All")}
              data-testid="route-depot-filter-clear"
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-blue-50 text-[#1E3A8A] border border-[#1E3A8A]/20 font-semibold hover:bg-blue-100">
              Depot: {depotFilter} <X size={10}/>
            </button>
          )}
          {busTypeFilter !== "All" && (
            <button onClick={() => setBusTypeFilter("All")}
              data-testid="route-bus-type-filter-clear"
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-blue-50 text-[#1E3A8A] border border-[#1E3A8A]/20 font-semibold hover:bg-blue-100">
              Type: {busTypeFilter} <X size={10}/>
            </button>
          )}
          {q.trim() && (
            <button onClick={() => setQ("")}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-blue-50 text-[#1E3A8A] border border-[#1E3A8A]/20 font-semibold hover:bg-blue-100">
              Search: &ldquo;{q}&rdquo; <X size={10}/>
            </button>
          )}
        </div>
      )}

      {/* Legacy/non-official bus types cleanup banner */}
      {nonOfficialBusTypes.length > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-3 px-3 py-2 rounded-md bg-amber-50 border border-amber-200" data-testid="non-official-bus-types-banner">
          <div className="text-[12px] min-w-0">
            <span className="font-bold text-amber-900">Heads up — </span>
            <span className="text-amber-900">{nonOfficialBusTypes.length} legacy bus type value(s) not in your official list: </span>
            <span className="font-mono text-amber-900 break-words">{nonOfficialBusTypes.join(", ")}</span>
          </div>
          <button onClick={clearNonOfficial}
            data-testid="clear-non-official-bus-types"
            className="self-start sm:self-auto px-3 py-1.5 rounded-md text-[11px] font-bold bg-amber-600 text-white hover:bg-amber-700 whitespace-nowrap">
            Clear them
          </button>
        </div>
      )}

      {/* Routes list — table on desktop, card stack on mobile */}
      <div className="border border-slate-200 rounded-md overflow-hidden">
        {/* Desktop / tablet: classic table */}
        <table className="w-full text-sm hidden md:table">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left font-bold">Route No.</th>
              <th className="px-3 py-2 text-left font-bold">Route Name</th>
              <th className="px-3 py-2 text-left font-bold">Depot</th>
              <th className="px-3 py-2 text-left font-bold">Bus Type</th>
              <th className="px-3 py-2 text-right font-bold w-28">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-400 italic">Loading…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-400 italic">
                {routes.length === 0 ? "No routes yet. Click \"Add Route\" to create the first one." : "No matches for that search."}
              </td></tr>
            )}
            {!loading && filtered.map((r) => (
              <tr key={r.route_id} className="border-t border-slate-100 hover:bg-blue-50/30" data-testid={`route-row-${r.route_id}`}>
                <td className="px-3 py-2 font-mono font-bold text-[#1E3A8A]">{r.route_no || "—"}</td>
                <td className="px-3 py-2">{r.name || "—"}</td>
                <td className="px-3 py-2">{r.depot || <span className="text-slate-400 italic">—</span>}</td>
                <td className="px-3 py-2">
                  {r.bus_type
                    ? <span className="inline-block px-2 py-0.5 rounded text-[10.5px] font-bold bg-blue-50 text-[#1E3A8A] border border-[#1E3A8A]/20">{r.bus_type}</span>
                    : <span className="text-slate-400 italic">—</span>}
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex items-center gap-1">
                    <button onClick={() => openEdit(r)} title="Edit"
                      data-testid={`route-edit-${r.route_id}`}
                      className="w-7 h-7 rounded inline-flex items-center justify-center text-[#1E3A8A] hover:bg-blue-50">
                      <PencilSimple size={14}/>
                    </button>
                    <button onClick={() => setConfirmDel(r)} title="Delete"
                      data-testid={`route-delete-${r.route_id}`}
                      className="w-7 h-7 rounded inline-flex items-center justify-center text-red-600 hover:bg-red-50">
                      <Trash size={14}/>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Mobile: card list */}
        <div className="md:hidden divide-y divide-slate-100">
          {loading && (
            <div className="px-3 py-8 text-center text-slate-400 italic text-sm">Loading…</div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="px-3 py-8 text-center text-slate-400 italic text-sm">
              {routes.length === 0 ? "No routes yet. Tap \"Add Route\" to create the first one." : "No matches."}
            </div>
          )}
          {!loading && filtered.map((r) => (
            <div key={r.route_id}
              data-testid={`route-card-${r.route_id}`}
              className="px-3 py-3 flex items-start gap-3 hover:bg-blue-50/30 active:bg-blue-50">
              {/* Route number pill */}
              <div className="flex-shrink-0 min-w-[44px] h-11 rounded-md bg-[#1E3A8A]/10 text-[#1E3A8A] flex items-center justify-center px-2">
                <span className="font-mono font-bold text-sm leading-none">{r.route_no || "—"}</span>
              </div>
              {/* Body */}
              <div className="flex-1 min-w-0 space-y-1">
                <div className="font-bold text-[13.5px] text-slate-900 break-words leading-tight">{r.name || "—"}</div>
                <div className="flex items-center gap-1.5 flex-wrap text-[11px] text-slate-500">
                  <span className="inline-flex items-center gap-1">
                    <Buildings size={11} weight="duotone"/>{r.depot || "—"}
                  </span>
                  {r.bus_type && (
                    <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-50 text-[#1E3A8A] border border-[#1E3A8A]/20">
                      {r.bus_type}
                    </span>
                  )}
                </div>
              </div>
              {/* Actions */}
              <div className="flex-shrink-0 flex items-center gap-1">
                <button onClick={() => openEdit(r)} title="Edit"
                  data-testid={`route-edit-${r.route_id}`}
                  className="w-9 h-9 rounded-md inline-flex items-center justify-center text-[#1E3A8A] bg-blue-50 active:bg-blue-100">
                  <PencilSimple size={16}/>
                </button>
                <button onClick={() => setConfirmDel(r)} title="Delete"
                  data-testid={`route-delete-${r.route_id}`}
                  className="w-9 h-9 rounded-md inline-flex items-center justify-center text-red-600 bg-red-50 active:bg-red-100">
                  <Trash size={16}/>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add / Edit modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-[1400] flex items-center justify-center p-4"
          data-testid="route-form-dialog"
          onClick={closeForm}>
          <div className="bg-white rounded-md max-w-md w-full p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-bold text-lg flex items-center gap-2">
              <Path size={18} weight="duotone" className="text-[#1E3A8A]"/>
              {editing ? "Edit Route" : "Add Route"}
            </h3>
            <label className="block">
              <span className="text-[10.5px] uppercase tracking-wider text-slate-500 font-bold">Route Number *</span>
              <input value={form.route_no}
                onChange={(e) => setForm({ ...form, route_no: e.target.value })}
                placeholder="e.g. 1, 21A, DBA-1"
                data-testid="route-field-no"
                autoFocus
                className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-md text-sm outline-none focus:border-[#1E3A8A]"/>
            </label>
            <label className="block">
              <span className="text-[10.5px] uppercase tracking-wider text-slate-500 font-bold">Route Name *</span>
              <input value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Panaji – Margao via Cortalim"
                data-testid="route-field-name"
                className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-md text-sm outline-none focus:border-[#1E3A8A]"/>
            </label>
            <label className="block">
              <span className="text-[10.5px] uppercase tracking-wider text-slate-500 font-bold">Depot *</span>
              <select value={form.depot}
                onChange={(e) => setForm({ ...form, depot: e.target.value })}
                data-testid="route-field-depot"
                className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-md text-sm outline-none focus:border-[#1E3A8A] bg-white">
                <option value="">— Select depot —</option>
                {depots.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
              <span className="text-[10px] text-slate-400 mt-0.5 block">
                Same route number can repeat across different depots (e.g. Route 1 at Panaji ≠ Route 1 at Margao).
              </span>
            </label>
            <label className="block">
              <span className="text-[10.5px] uppercase tracking-wider text-slate-500 font-bold">Bus Type</span>
              <select value={form.bus_type}
                onChange={(e) => setForm({ ...form, bus_type: e.target.value })}
                data-testid="route-field-bus-type"
                className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-md text-sm outline-none focus:border-[#1E3A8A] bg-white">
                <option value="">— Select bus type —</option>
                {busTypes.map((bt) => <option key={bt.id} value={bt.name}>{bt.name}</option>)}
              </select>
              <span className="text-[10px] text-slate-400 mt-0.5 block">
                Need a new type? Click <b>Manage Bus Types</b> on the routes panel.
              </span>
            </label>
            <div className="flex gap-2 pt-2">
              <button onClick={closeForm} disabled={saving}
                className="flex-1 px-3 py-2 rounded-md text-sm border border-slate-200 hover:bg-slate-50 disabled:opacity-50">
                Cancel
              </button>
              <button onClick={save} disabled={saving}
                data-testid="route-form-save"
                className="ktcl-btn-primary flex-1 px-3 py-2 rounded-md text-sm font-bold disabled:opacity-50">
                {saving ? "Saving…" : (editing ? "Save Changes" : "Add Route")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {confirmDel && (
        <div className="fixed inset-0 bg-black/50 z-[1400] flex items-center justify-center p-4"
          data-testid="route-delete-dialog"
          onClick={() => setConfirmDel(null)}>
          <div className="bg-white rounded-md max-w-sm w-full p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-bold text-lg flex items-center gap-2">
              <Warning size={18} weight="duotone" className="text-red-600"/>
              Delete route?
            </h3>
            <p className="text-sm text-slate-600">
              This will permanently remove <b>{confirmDel.route_no}</b> — {confirmDel.name}.
              You cannot undo this.
            </p>
            <div className="flex gap-2 pt-1">
              <button onClick={() => setConfirmDel(null)}
                className="flex-1 px-3 py-2 rounded-md text-sm border border-slate-200 hover:bg-slate-50">
                Cancel
              </button>
              <button onClick={del}
                data-testid="route-delete-confirm"
                className="flex-1 px-3 py-2 rounded-md text-sm font-bold bg-red-600 text-white hover:bg-red-700">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manage Bus Types modal */}
      {showBT && (
        <div className="fixed inset-0 bg-black/50 z-[1400] flex items-center justify-center p-4"
          data-testid="bus-types-dialog"
          onClick={() => setShowBT(false)}>
          <div className="bg-white rounded-md max-w-md w-full p-5 space-y-3 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-lg flex items-center gap-2">
                <Bus size={18} weight="duotone" className="text-[#1E3A8A]"/>
                Manage Bus Types
              </h3>
              <button onClick={() => setShowBT(false)} className="text-slate-400 hover:text-slate-700">
                <X size={16}/>
              </button>
            </div>
            <p className="text-[12px] text-slate-500">
              These appear in the <b>Bus Type</b> dropdown when adding or editing a route.
              Defaults can&apos;t be deleted; types in use by existing routes can&apos;t be deleted either.
            </p>

            {/* Add new */}
            <div className="flex items-center gap-2">
              <input value={newBT}
                onChange={(e) => setNewBT(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") addBusType(); }}
                placeholder="e.g. SLEEPER COACH"
                data-testid="bus-type-new-input"
                className="flex-1 px-3 py-2 border border-slate-200 rounded-md text-sm outline-none focus:border-[#1E3A8A] uppercase"/>
              <button onClick={addBusType} disabled={savingBT}
                data-testid="bus-type-add-btn"
                className="ktcl-btn-primary px-3 py-2 rounded-md text-[12px] font-bold inline-flex items-center gap-1.5 disabled:opacity-50">
                <Plus size={13} weight="bold"/> {savingBT ? "Adding…" : "Add"}
              </button>
            </div>

            {/* List */}
            <div className="border border-slate-200 rounded-md overflow-hidden">
              {busTypes.length === 0 && (
                <div className="px-3 py-6 text-center text-xs text-slate-400 italic">No bus types yet.</div>
              )}
              {busTypes.map((bt) => (
                <div key={bt.id} className="px-3 py-2 flex items-center justify-between border-b border-slate-100 last:border-0">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-mono font-bold text-[#1E3A8A]">{bt.name}</span>
                    {bt.is_default && (
                      <span className="text-[9.5px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">
                        default
                      </span>
                    )}
                  </div>
                  {!bt.is_default && (
                    <button onClick={() => removeBusType(bt)}
                      data-testid={`bus-type-delete-${bt.id}`}
                      title="Remove"
                      className="w-7 h-7 rounded inline-flex items-center justify-center text-red-600 hover:bg-red-50">
                      <Trash size={13}/>
                    </button>
                  )}
                </div>
              ))}
            </div>

            <button onClick={() => setShowBT(false)}
              className="w-full px-3 py-2 rounded-md text-sm border border-slate-200 hover:bg-slate-50">
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


function PreviewStat({ label, value, tone = "info", testid }) {
  const tones = {
    info: "bg-slate-50 border-slate-200 text-slate-900",
    ok:   "bg-emerald-50 border-emerald-200 text-emerald-800",
    warn: "bg-amber-50 border-amber-200 text-amber-900",
  };
  return (
    <div className={`rounded-md border px-3 py-2 ${tones[tone]}`}>
      <div className="text-[10px] uppercase tracking-wider font-bold opacity-80">{label}</div>
      <div className="font-display font-extrabold text-xl tabular-nums" data-testid={testid}>{value ?? 0}</div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Fleet Report Export — pulls the current `buses` collection out of Mongo
// and lets the operator download it in CSV / XLSX / PDF / JSON.
//
// Filters (depot · category · in_service) drive both the live summary
// rendered above the buttons AND the file that gets downloaded — so the
// operator sees exactly what they'll get before they click "Download".
// ---------------------------------------------------------------------------
function FleetReportExport() {
  const [depot, setDepot] = useState("");
  const [category, setCategory] = useState("");
  const [serviceFilter, setServiceFilter] = useState("");  // "" | "active" | "inactive"
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState("");      // "csv" | "xlsx" | "pdf" | "json" | ""
  const [depotOptions, setDepotOptions] = useState([]);

  // Build query string from current filters
  const filtersToParams = () => {
    const p = new URLSearchParams();
    if (depot) p.set("depot", depot);
    if (category) p.set("category", category);
    if (serviceFilter === "active") p.set("in_service", "true");
    if (serviceFilter === "inactive") p.set("in_service", "false");
    return p.toString();
  };

  // Refresh the live count + breakdowns whenever any filter changes.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const qs = filtersToParams();
    api.get(`/admin/fleet-export/summary${qs ? `?${qs}` : ""}`)
      .then(({ data }) => { if (!cancelled) setSummary(data); })
      .catch(() => { if (!cancelled) setSummary(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [depot, category, serviceFilter]);

  // Populate the depot dropdown from the first summary's by_depot keys
  useEffect(() => {
    if (depotOptions.length > 0 || !summary) return;
    const keys = Object.keys(summary?.summary?.by_depot || {}).sort();
    if (keys.length) setDepotOptions(keys);
  }, [summary, depotOptions.length]);

  const download = async (fmt) => {
    if (!summary || summary.row_count === 0) {
      toast.error("Nothing to export — the filtered set is empty");
      return;
    }
    setDownloading(fmt);
    try {
      const qs = filtersToParams();
      const url = `/admin/fleet-export/download.${fmt}${qs ? `?${qs}` : ""}`;
      const { data, headers } = await api.get(url, { responseType: "blob" });
      const blob = new Blob([data], { type: headers?.["content-type"] || "application/octet-stream" });
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      // Try to honour the server's filename
      const cd = headers?.["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : `ktcl_fleet_report.${fmt}`;
      a.click();
      URL.revokeObjectURL(blobUrl);
      toast.success(`Downloaded ${fmt.toUpperCase()} · ${summary.row_count} rows`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to download ${fmt.toUpperCase()}`);
    } finally {
      setDownloading("");
    }
  };

  const s = summary?.summary;
  const totalRows = summary?.row_count ?? 0;

  return (
    <div className="ktcl-card p-4 md:p-5 space-y-3" data-testid="fleet-export-card">
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-md bg-blue-50 border border-[#1E3A8A]/20 flex items-center justify-center shrink-0">
          <DownloadSimple size={26} weight="duotone" className="text-[#1E3A8A]"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-display font-bold text-base md:text-lg">
              Fleet Report — Download
            </h2>
            <span className="text-[10px] uppercase tracking-wider font-bold rounded px-1.5 py-0.5 bg-blue-100 text-[#1E3A8A]">
              CSV · XLSX · PDF · JSON
            </span>
          </div>
          <p className="text-[12px] text-slate-500 mt-0.5">
            Export the current state of the <code className="font-mono">buses</code> collection in any format.
            Filters refine the file in real time — the count below shows exactly how many records will be downloaded.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold flex items-center gap-1">
            <Funnel size={11} weight="bold"/> Depot
          </span>
          <select value={depot} onChange={(e) => setDepot(e.target.value)}
            data-testid="fleet-export-filter-depot"
            className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-md text-sm outline-none focus:border-[#1E3A8A] bg-white">
            <option value="">All depots</option>
            {depotOptions.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold flex items-center gap-1">
            <Funnel size={11} weight="bold"/> Category
          </span>
          <select value={category} onChange={(e) => setCategory(e.target.value)}
            data-testid="fleet-export-filter-category"
            className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-md text-sm outline-none focus:border-[#1E3A8A] bg-white">
            <option value="">All categories</option>
            <option value="Bus">Bus</option>
            <option value="Utility">Utility (cars / cranes)</option>
          </select>
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold flex items-center gap-1">
            <Funnel size={11} weight="bold"/> Service status
          </span>
          <select value={serviceFilter} onChange={(e) => setServiceFilter(e.target.value)}
            data-testid="fleet-export-filter-service"
            className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-md text-sm outline-none focus:border-[#1E3A8A] bg-white">
            <option value="">All</option>
            <option value="active">Active / In service</option>
            <option value="inactive">Scrap / Out of service</option>
          </select>
        </label>
      </div>

      {/* Live counts */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="fleet-export-summary">
        <PreviewStat label="Rows to download" value={totalRows}
                     testid="fleet-export-total" tone="info"/>
        <PreviewStat label="Active / In service" value={s?.active ?? 0}
                     testid="fleet-export-active" tone="ok"/>
        <PreviewStat label="Scrap / Inactive" value={s?.scrap_or_inactive ?? 0}
                     testid="fleet-export-scrap" tone="warn"/>
        <PreviewStat label="Depots in scope" value={Object.keys(s?.by_depot || {}).length}
                     testid="fleet-export-depots" tone="info"/>
      </div>

      {/* Breakdown chips */}
      {s && Object.keys(s.by_depot || {}).length > 0 && (
        <div className="bg-slate-50 border border-slate-200 rounded-md p-3 space-y-2"
             data-testid="fleet-export-breakdown">
          <div className="flex flex-wrap gap-1.5 items-center">
            <span className="text-[10px] uppercase tracking-wider font-bold text-slate-600">By depot:</span>
            {Object.entries(s.by_depot).map(([d, n]) => (
              <span key={d} className="text-[11.5px] font-mono bg-white border border-[#1E3A8A]/20 text-[#1E3A8A] rounded px-2 py-0.5">
                <b>{d}</b> · {n}
              </span>
            ))}
          </div>
          {Object.keys(s.by_bs_emission || {}).length > 0 && (
            <div className="flex flex-wrap gap-1.5 items-center">
              <span className="text-[10px] uppercase tracking-wider font-bold text-slate-600">By BS:</span>
              {Object.entries(s.by_bs_emission).map(([k, n]) => (
                <span key={k} className="text-[11.5px] font-mono bg-white border border-slate-200 rounded px-2 py-0.5">
                  {k} · {n}
                </span>
              ))}
            </div>
          )}
          {Object.keys(s.by_category || {}).length > 0 && (
            <div className="flex flex-wrap gap-1.5 items-center">
              <span className="text-[10px] uppercase tracking-wider font-bold text-slate-600">By category:</span>
              {Object.entries(s.by_category).map(([k, n]) => (
                <span key={k} className="text-[11.5px] font-mono bg-white border border-slate-200 rounded px-2 py-0.5">
                  {k} · {n}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {totalRows === 0 && !loading && (
        <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-[12px] text-amber-800"
             data-testid="fleet-export-empty">
          No matching buses in the database. Import the KTCL Fleet Chart above, or relax your filters.
        </div>
      )}

      {/* Format buttons */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <button onClick={() => download("csv")} disabled={!!downloading || totalRows === 0}
          data-testid="fleet-export-download-csv"
          className="px-3 py-2.5 rounded-md text-[12px] font-bold bg-blue-50 text-[#1E3A8A] border border-[#1E3A8A]/20 hover:bg-blue-100 disabled:opacity-50 inline-flex items-center justify-center gap-1.5">
          <FileCsv size={14} weight="bold"/>{downloading === "csv" ? "Building…" : "Download CSV"}
        </button>
        <button onClick={() => download("xlsx")} disabled={!!downloading || totalRows === 0}
          data-testid="fleet-export-download-xlsx"
          className="px-3 py-2.5 rounded-md text-[12px] font-bold bg-blue-50 text-[#1E3A8A] border border-[#1E3A8A]/20 hover:bg-blue-100 disabled:opacity-50 inline-flex items-center justify-center gap-1.5">
          <FileXls size={14} weight="bold"/>{downloading === "xlsx" ? "Building…" : "Download XLSX"}
        </button>
        <button onClick={() => download("pdf")} disabled={!!downloading || totalRows === 0}
          data-testid="fleet-export-download-pdf"
          className="px-3 py-2.5 rounded-md text-[12px] font-bold bg-blue-50 text-[#1E3A8A] border border-[#1E3A8A]/20 hover:bg-blue-100 disabled:opacity-50 inline-flex items-center justify-center gap-1.5">
          <FilePdf size={14} weight="bold"/>{downloading === "pdf" ? "Rendering…" : "Download PDF"}
        </button>
        <button onClick={() => download("json")} disabled={!!downloading || totalRows === 0}
          data-testid="fleet-export-download-json"
          className="ktcl-btn-primary px-3 py-2.5 rounded-md text-[12px] font-bold disabled:opacity-50 inline-flex items-center justify-center gap-1.5">
          <FileText size={14} weight="bold"/>{downloading === "json" ? "Building…" : "Download JSON"}
        </button>
      </div>
    </div>
  );
}

